import { useCallback, useState } from 'react';

import {
  CANCELLED,
  DragId,
  DragItem,
  DropItem,
  KeyboardInteraction,
  MOVE_LEFT,
  MOVE_RIGHT,
  PICKUP,
  PLACED,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import {
  addItemToDropZone,
  findDropItemByPreviewId,
  isEveryDraggableDropped,
  isEveryDropAreaOccupied,
  markDragItemAsDropped,
  moveToFreePosition,
  removeItemFromDropZone,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  checkAnswer,
  checkAnswers,
} from '../../shared/interactive-response-shared.utils';
import { InteractiveCategoriseAttempt } from '../interactive-response-categorise.constants';

export const useCategorise = ({
  playClick,
  isCheckAnswerAtEnd,
  showAnswer,
  onAnswerAttempt,
  onShowSubmitButton,
}: {
  isCheckAnswerAtEnd: boolean;
  showAnswer: boolean;
  playClick: () => void;
  onAnswerAttempt: InteractiveCategoriseAttempt;
  onShowSubmitButton?: () => void;
}) => {
  const [dropItems, setDropItems] = useState<DropItem[]>([]);
  const [dragItems, setDragItems] = useState<DragItem[]>([]);
  const [keyboardState, setKeyboardState] = useState<KeyboardInteraction>();
  const [lastDropLocation, setLastDropLocation] = useState<number>(-1);

  const handleOnDrop = useCallback(
    (destinationId: DragId, sourceId: DragId) => {
      const dropItem = dropItems.find(item => item.id === destinationId);
      const dragItem = dragItems.find(item => item.id === sourceId);

      if (dragItem && dropItem) {
        if (!dragItem.isDropped) {
          addItemToDropZone(dropItem, dragItem);
          setDropItems([...dropItems]);
          dragItem.isDropped = true;
          setDragItems([...dragItems]);
          const isEverythingAnswered = isEveryDraggableDropped(dragItems);

          if (showAnswer && !isCheckAnswerAtEnd) {
            // check the answer now
            checkAnswer(dropItem, dragItem);
            setDropItems([...dropItems]);

            if (isEverythingAnswered) {
              onAnswerAttempt(dropItems);
            }
          } else if (
            !showAnswer &&
            isEverythingAnswered &&
            onShowSubmitButton
          ) {
            onShowSubmitButton();
          }
        } else {
          removeItemFromDropZone(dropItems, sourceId);
          addItemToDropZone(dropItem, dragItem);
          setDropItems(() => [...dropItems]);
        }
      }
      playClick();
    },
    [
      dropItems,
      dragItems,
      isCheckAnswerAtEnd,
      showAnswer,
      playClick,
      onAnswerAttempt,
      onShowSubmitButton,
    ]
  );

  const handleItemPickUp = useCallback(
    (interaction: KeyboardInteraction) => {
      setLastDropLocation(
        dropItems.findIndex(item =>
          item.zones.find(l => l.dragItemId === interaction.id)
        )
      );
      setKeyboardState(interaction);
    },
    [dropItems]
  );

  const handleItemCancelled = useCallback(
    (interaction: KeyboardInteraction) => {
      if (lastDropLocation === -1) {
        removeItemFromDropZone(dropItems, interaction.id);
        const dragIdx = dragItems.findIndex(item => item.id === interaction.id);
        dragItems[dragIdx].isDropped = false;
        setDragItems([...dragItems]);
      } else {
        const location = dropItems[lastDropLocation].zones.find(
          z => z.previewItemId === interaction.id
        );
        if (location) {
          location.dragItemId = interaction.id;
        }
        setDropItems([...dropItems]);
      }
      setKeyboardState(interaction);
    },
    [dropItems, dragItems, lastDropLocation]
  );

  const handleItemPlacedDown = useCallback(
    (interaction: KeyboardInteraction) => {
      const dropItem = findDropItemByPreviewId(dropItems, interaction.id);
      if (dropItem) {
        const zone = dropItem.zones.find(
          z => z.previewItemId === interaction.id
        );
        zone!.previewItemId = null;
        zone!.dragItemId = interaction.id;
        setDropItems([...dropItems]);
        interaction.announce = `${interaction.announce} ${dropItem.label}`;
        setKeyboardState({ ...interaction });
        playClick();

        if (showAnswer && !isCheckAnswerAtEnd) {
          // check the answer now
          const dragItem = dragItems.find(d => d.id === interaction.id);
          if (dragItem) checkAnswer(dropItem, dragItem);
          setDropItems([...dropItems]);
        }
      } else {
        return;
      }
    },
    [dropItems, dragItems, isCheckAnswerAtEnd, showAnswer, playClick]
  );

  const handleItemMove = useCallback(
    (interaction: KeyboardInteraction) => {
      if (isEveryDropAreaOccupied(dropItems)) {
        return;
      }
      const { dropItems: updatedItems, movedTo } = moveToFreePosition(
        interaction.id,
        dropItems,
        interaction.action === MOVE_LEFT ? 'left' : 'right'
      );
      setDropItems([...updatedItems]);
      setDragItems([...markDragItemAsDropped(interaction.id, dragItems)]);

      interaction.announce = `${interaction.announce} ${dropItems[movedTo].label}`;
      setKeyboardState(interaction);
    },
    [dropItems, dragItems]
  );

  const handleKeyboardInteraction = useCallback(
    (interaction: KeyboardInteraction) => {
      if (interaction.action === PICKUP) {
        handleItemPickUp(interaction);
      }

      if (interaction.action === CANCELLED) {
        handleItemCancelled(interaction);
      }

      if (interaction.action === PLACED) {
        handleItemPlacedDown(interaction);
      }

      if (
        interaction.action === MOVE_LEFT ||
        interaction.action === MOVE_RIGHT
      ) {
        handleItemMove(interaction);
      }
    },
    [
      handleItemPickUp,
      handleItemCancelled,
      handleItemPlacedDown,
      handleItemMove,
    ]
  );

  const handleCheckAnswers = useCallback(() => {
    setDropItems(() => [...checkAnswers(dropItems, dragItems)]);
    onAnswerAttempt(dropItems);
  }, [dropItems, dragItems, setDropItems, onAnswerAttempt]);

  return {
    keyboardState,
    dragItems,
    dropItems,
    setDragItems,
    setDropItems,
    handleOnDrop,
    handleCheckAnswers,
    handleKeyboardInteraction,
  };
};
